import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';
import Button from './Button';

const UploadModal = ({ isOpen, onClose, onUpload, isActuallyUploading = false }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = (event) => {
    const files = Array.from(event?.target?.files);
    setSelectedFiles(files);
  };

  const handleDrop = (event) => {
    event?.preventDefault();
    const files = Array.from(event?.dataTransfer?.files);
    setSelectedFiles(files);
  };

  const handleDragOver = (event) => {
    event?.preventDefault();
  };

  const handleUpload = async () => {
    if (selectedFiles?.length === 0) return;

    setIsUploading(true);

    // Call the parent's onUpload function with actual files
    if (onUpload) {
      await onUpload(selectedFiles);
    }

    setIsUploading(false);
    handleClose();
  };

  const handleClose = () => {
    if (isUploading || isActuallyUploading) return; // Prevent closing during upload
    setSelectedFiles([]);
    setUploadProgress({});
    setIsUploading(false);
    onClose();
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles?.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    let i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  if (!isOpen) return null;

  const uploadInProgress = isUploading || isActuallyUploading;

  return createPortal(
    <div className="modal-overlay animate-fade-in" onClick={!uploadInProgress ? handleClose : undefined}>
      <div className="modal-content animate-scale-in" onClick={(e) => e?.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Upload Files to S3</h2>
          <button 
            onClick={handleClose} 
            disabled={uploadInProgress}
            className="focus-ring rounded-md p-1 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div
            className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors duration-200"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <Icon name="Upload" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-2">
              Drag and drop files here, or click to select
            </p>
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="file-input"
              disabled={uploadInProgress}
            />
            <label htmlFor="file-input">
              <Button variant="outline" className="mt-2" disabled={uploadInProgress} asChild>
                <span>Select Files</span>
              </Button>
            </label>
          </div>

          {selectedFiles?.length > 0 && (
            <div className="mt-6 space-y-3">
              <h3 className="text-sm font-medium text-foreground">
                Selected Files ({selectedFiles?.length})
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin">
                {selectedFiles?.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-muted rounded-md"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Icon name="File" size={20} className="text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {file?.name}
                        </p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {formatFileSize(file?.size)}
                        </p>
                        {uploadInProgress && (
                          <p className="text-xs text-primary mt-1 animate-pulse">
                            Uploading to S3...
                          </p>
                        )}
                      </div>
                    </div>
                    {!uploadInProgress && (
                      <button
                        onClick={() => removeFile(index)}
                        className="focus-ring rounded-md p-1 hover:bg-background ml-2"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <Button variant="outline" onClick={handleClose} disabled={uploadInProgress}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={selectedFiles?.length === 0 || uploadInProgress}
            loading={uploadInProgress}
          >
            {uploadInProgress ? 'Uploading to S3...' : `Upload ${selectedFiles?.length > 0 ? `(${selectedFiles?.length})` : ''}`}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UploadModal;